import React, { useMemo } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Scroll, Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { TABLET_TAB_BAR_CLEARANCE } from '@/features/guardian/constants';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';
import BookClassButton from './book-class-button';
import UpcomingWeekCard from './card';
import UpcomingWeekEmptyState from './empty-state';
import type { UpcomingWeekSectionProps } from './types';
import { groupUpcomingWeekItemsByDate } from './utils';

const strings = STRINGS.guardian.home.upcomingWeek;

function UpcomingWeekSection({
  items = [],
  onBookClassPress,
  onItemPress,
  style,
}: UpcomingWeekSectionProps) {
  const { isTablet, scale } = useScaling();
  const { bottom } = useSafeAreaInsets();
  const styles = useStyles();
  const hasItems = items.length > 0;

  const groups = useMemo(() => groupUpcomingWeekItemsByDate(items), [items]);

  const scheduleCards = isTablet
    ? groups.map(group => (
        <UpcomingWeekCard key={group.key} items={group.items} onItemPress={onItemPress} />
      ))
    : [<UpcomingWeekCard key="mobile-upcoming-week" items={items} onItemPress={onItemPress} />];

  return (
    <Stack style={[styles.root, style]}>
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.sectionTitle}>
        {strings.title}
      </Text>

      {hasItems ? (
        isTablet ? (
          <Scroll
            flex={1}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={[
              styles.scrollContent,
              { paddingBottom: bottom + scale(TABLET_TAB_BAR_CLEARANCE) },
            ]}>
            <Stack gap={scale(10)}>{scheduleCards}</Stack>
            <BookClassButton onPress={onBookClassPress} style={styles.bookClassButton} />
          </Scroll>
        ) : (
          <Stack style={styles.mobileContent}>{scheduleCards}</Stack>
        )
      ) : (
        <UpcomingWeekEmptyState onBookClassPress={onBookClassPress} />
      )}
    </Stack>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  root: {
    backgroundColor: guardianColors['1'],
    paddingTop: scale(16),
    paddingHorizontal: isTablet ? undefined : scale(20),
  },
  sectionTitle: {
    color: guardianColors['10'],
  },
  scrollContent: {
    marginTop: scale(8),
  },
  mobileContent: {
    marginTop: scale(8),
  },
  bookClassButton: {
    marginTop: scale(8),
  },
}));

export { MOCK_UPCOMING_WEEK_ITEMS } from './mock-data';
export type { UpcomingWeekItem, UpcomingWeekSectionProps } from './types';
export default UpcomingWeekSection;
